import { FC, useMemo, useState } from 'react';
import { Outlet } from 'react-router-dom';
import { Sidebar } from './layout/Sidebar';
import { ExternalUsersSidebar } from './layout/ExternalUsersSidebar';
import { LayoutDashboard, Lightbulb, Megaphone, Handshake, MailIcon, Activity, CreditCard } from 'lucide-react';
import { Header } from './layout/Header';
import { useLogout, useUser } from '@/hooks';

/**
 * Main Layout Component
 * Provides the application shell with sidebar, header, and content area
 * Features responsive design with mobile menu toggle and side-by-side layout
 */
const Layout: FC = () => {
  const { logout } = useLogout();
  const user = useUser();
  const [isSidebarOpen, setIsSidebarOpen] = useState(false);

  // Close sidebar when clicking outside on mobile
  const handleOverlayClick = (): void => {
    setIsSidebarOpen(false);
  };

  const toggleSidebar = (): void => {
    setIsSidebarOpen(!isSidebarOpen);
  };

  const navItems = useMemo(() => {
    // We will let Sidebar use defaults via undefined; when role is internal/admin, we pass extended list
    if (user?.role === 'internal' || user?.role === 'admin') {
      return [
        { to: '/dashboard', label: 'Dashboard', icon: <LayoutDashboard className="w-5 h-5" /> },
        { to: '/topics', label: 'Topics Automation', icon: <Lightbulb className="w-5 h-5" /> },
        { to: '/marketing', label: 'Marketing Automation', icon: <Megaphone className="w-5 h-5" /> },
        { to: '/sales', label: 'Sales Automation', icon: <Handshake className="w-5 h-5" /> },
        { to: '/emaillogs', label: 'Email Logs', icon: <MailIcon className="w-5 h-5" /> },
        { to: '/payments', label: 'Payments & Subscriptions', icon: <CreditCard className="w-5 h-5" /> },
        { to: '/scheduler-logs', label: 'Scheduler Runs', icon: <Activity className="w-5 h-5" /> },
      ];
    }
    return undefined;
  }, [user?.role]);

  return (
    <div className="h-screen flex bg-gray-50 overflow-hidden">
      {/* Mobile Overlay */}
      {isSidebarOpen && (
        <div
          className="fixed inset-0 bg-gray-900/50 z-20 lg:hidden"
          onClick={handleOverlayClick}
          aria-hidden="true"
        />
      )}

      {/* External Users Sidebar - Part of flex layout - Only for internal roles */}
      {(user?.role === 'internal' || user?.role === 'admin') && <ExternalUsersSidebar />}

      {/* Main Sidebar Navigation - Part of flex layout */}
      <Sidebar
        isOpen={isSidebarOpen}
        onClose={() => setIsSidebarOpen(false)}
        navItems={navItems}
      />

      {/* Main Content Area - Takes remaining space */}
      <div className="flex-1 flex flex-col min-w-0">
        {/* Header */}
        <Header
          onLogout={logout}
          userName={user?.name}
          onMenuToggle={toggleSidebar}
        />

        {/* Main Content */}
        <main className="flex-1 overflow-y-auto bg-gray-50 relative">
          <Outlet />
        </main>
      </div>
    </div>
  );
};

export default Layout;